import { SellingZoneGraphItem } from '@substratahq/selling-zone/typing/base';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMixPanel } from '../../../../../../../components/MixPanel/useMixPanel';
import { updateThreadComplete } from '../../../../../../../store/deals/deals.actions';
import { DetailedAnalysisEmail } from '../../../../../../../store/deals/deals.types';
import { RootState } from '../../../../../../../store/store';
import { PaginationArrow } from '../paginationArrow/PaginationArrow';

type Props = {
  currentEmail: DetailedAnalysisEmail | null;
  emailsInBranch?: number;
  isThreadComplete?: boolean;
  isNextMove?: boolean;
  selectedGraphItem?: SellingZoneGraphItem | null;
  onLeftArrowClick: () => void;
  onRightArrowClick: () => void;
};

export const EmailFooter: React.FC<Props> = ({
  currentEmail,
  emailsInBranch,
  isThreadComplete,
  isNextMove,
  onLeftArrowClick,
  onRightArrowClick,
}): JSX.Element => {
  const user = useSelector((state: RootState) => state.auth.user);
  const details = useSelector(
    (state: RootState) => state.deals.detailedAnalysis?.deal
  );

  const dispatch = useDispatch();

  const { trackEvent } = useMixPanel();

  const { pathname } = useLocation();

  const isInDetailedView =
    pathname.includes('example') && pathname.includes('threads');

  const paginationValue =
    (currentEmail?.index || currentEmail?.index === 0) &&
    (emailsInBranch || emailsInBranch === 0)
      ? `${currentEmail.index + 1} / ${emailsInBranch}`
      : '';

  const isLeftArrowdisabled =
    !currentEmail || !emailsInBranch || currentEmail?.index === 0;

  const isRightArrowDisabled =
    !currentEmail ||
    !emailsInBranch ||
    currentEmail?.index === emailsInBranch - 1;

  const openCurrentThreadInGmail = () => {
    if (
      currentEmail?.participants?.leftParticipant?.email &&
      currentEmail?.platformMessageId
    ) {
      const messageId = currentEmail.platformMessageId;

      window.open(
        `https://mail.google.com/mail/u/${user?.email}/#inbox/${messageId}`,
        '_blank'
      );

      trackEvent('go-to-thread-clicked');
    }
  };

  const yesButtonClick = () => {
    if (user?.userId && user?.orgId) {
      const { orgId, userId } = user;
      const threadId = details?.platformThreadId || '';

      if (!isThreadComplete && isNextMove) {
        dispatch(updateThreadComplete(orgId, userId, threadId, true));
      }
    }
  };

  const noButtonClick = () => {
    if (user?.userId && user?.orgId) {
      const { orgId, userId } = user;
      const threadId = details?.platformThreadId || '';

      if (isThreadComplete) {
        dispatch(updateThreadComplete(orgId, userId, threadId, false));
      }
    }
  };

  const attrs = {
    wrapper: {
      className: `${isInDetailedView ? 'disabled' : ''} email-footer`,
    },
    pagination: {
      wrapper: { className: 'email-footer__pagination' },
      leftArrow: {
        className: 'email-footer__pagination-left',
        onClick: onLeftArrowClick,
        disabled: isLeftArrowdisabled,
      },
      rightArrow: {
        className: 'email-footer__pagination-right',
        onClick: onRightArrowClick,
        disabled: isRightArrowDisabled,
        rotated: true,
      },
      pagesText: { className: 'email-footer__pagination-pages' },
    },
    rightSection: {
      wrapper: { className: 'email-footer__right-section' },
      thread: {
        wrapper: { className: 'email-footer__right-section__thread' },
        text: { className: 'email-footer__right-section__thread-text' },
        buttons: {
          wrapper: {
            className: 'email-footer__right-section__thread__buttons',
          },
          yes: {
            className: isThreadComplete
              ? 'active email-footer__right-section__thread__buttons-button'
              : 'email-footer__right-section__thread__buttons-button',
            onClick: yesButtonClick,
            disabled: isThreadComplete || !isNextMove,
          },
          no: {
            className: 'email-footer__right-section__thread__buttons-button',
            onClick: noButtonClick,
            disabled: !isThreadComplete,
          },
        },
      },

      link: {
        className: 'email-footer__right-section-link',
        onClick: openCurrentThreadInGmail,
      },
    },
  };

  return (
    <div {...attrs.wrapper}>
      <div {...attrs.pagination.wrapper}>
        <PaginationArrow {...attrs.pagination.leftArrow} />
        <span {...attrs.pagination.pagesText}>{paginationValue}</span>
        <PaginationArrow {...attrs.pagination.rightArrow} />
      </div>
      <div {...attrs.rightSection.wrapper}>
        <div {...attrs.rightSection.thread.wrapper}>
          <span {...attrs.rightSection.thread.text}>Thread complete?</span>
          <div {...attrs.rightSection.thread.buttons.wrapper}>
            <button {...attrs.rightSection.thread.buttons.yes}>Yes</button>
            <button {...attrs.rightSection.thread.buttons.no}>No</button>
          </div>
        </div>
        <div {...attrs.rightSection.link}>Go to thread {'>'}</div>
      </div>
    </div>
  );
};
